const RELOAD_KEY_PREFIX = "forkto.preload-error-reload:";
const RELOAD_PENDING_MS = 5000;
const reloadingWindows = new WeakMap();

export function isPreloadRecoveryNavigationError(
    error,
    windowRef = typeof window !== "undefined" ? window : undefined
) {
    // Vite resolves a cancelled preload failure to undefined. Vue Router then
    // rejects the missing component before the requested reload unloads the page.
    const reloadUntil = reloadingWindows.get(windowRef);
    return (
        typeof reloadUntil === "number" &&
        Date.now() < reloadUntil &&
        typeof error?.message === "string" &&
        /^Couldn't resolve component "[^"]+" at "[^"]+"$/.test(error.message)
    );
}

export function filterPreloadRecoverySentryEvent(event, hint, windowRef) {
    // Sentry's Vue Router integration captures navigation errors independently
    // of our router.onError handler. Filter at the shared SDK boundary too.
    return isPreloadRecoveryNavigationError(hint?.originalException, windowRef) ? null : event;
}

function errorMessage(payload) {
    return typeof payload?.message === "string"
        ? payload.message
        : typeof payload === "string"
          ? payload
          : "";
}

function preloadErrorKey(payload) {
    const message = errorMessage(payload);
    return `${RELOAD_KEY_PREFIX}${message || "unknown"}`;
}

function getSessionStorage(windowRef) {
    try {
        return windowRef?.sessionStorage;
    } catch {
        return undefined;
    }
}

export function markPreloadErrorForReload(storage, payload) {
    const key = preloadErrorKey(payload);

    try {
        if (storage?.getItem(key) === "1") {
            return false;
        }
        storage?.setItem(key, "1");
    } catch {
        // Storage can be unavailable in private browsing or restricted webviews.
    }

    return true;
}

export function installPreloadErrorRecovery({
    windowRef = typeof window !== "undefined" ? window : undefined,
    storage,
    reload = () => windowRef?.location?.reload(),
    addBreadcrumb,
    captureException,
    captureMessage
} = {}) {
    if (!windowRef?.addEventListener) {
        return () => {};
    }

    const reloadStorage = storage ?? getSessionStorage(windowRef);
    let pendingReloadTimer;
    const report = (payload, outcome, reloadError) => {
        const context = {
            outcome,
            error: errorMessage(payload).slice(0, 1000),
            online: windowRef?.navigator?.onLine,
            build_id: import.meta.env.VITE_BUILD_ID,
            ...(reloadError ? { reload_error: errorMessage(reloadError).slice(0, 1000) } : {})
        };
        try {
            captureException?.(payload, {
                tags: { error_source: "vite_preload" },
                contexts: { preload_recovery: context }
            });
        } catch {
            // Diagnostics must not interrupt recovery or hide the Vite error.
        }
        return context;
    };
    const onPreloadError = (event) => {
        clearTimeout(pendingReloadTimer);
        const shouldReload = markPreloadErrorForReload(reloadStorage, event.payload);
        if (!shouldReload) {
            reloadingWindows.delete(windowRef);
            report(event.payload, "reload_already_attempted");
            return;
        }

        try {
            addBreadcrumb?.({
                category: "app.update",
                level: "info",
                message: "Reloading after Vite preload error",
                data: {
                    error: errorMessage(event.payload).slice(0, 1000)
                }
            });
        } catch {
            // A failed telemetry hook must not prevent the guarded reload.
        }
        try {
            reload();
        } catch (error) {
            reloadingWindows.delete(windowRef);
            report(event.payload, "reload_failed", error);
            // Leave the event uncancelled so Vite throws the original failure.
            return;
        }

        event.preventDefault();
        reloadingWindows.set(windowRef, Date.now() + RELOAD_PENDING_MS);
        const context = report(event.payload, "reload_requested");
        if (!captureMessage) return;
        pendingReloadTimer = setTimeout(() => {
            reloadingWindows.delete(windowRef);
            // A successful reload destroys this document and its timers. If it
            // remains alive, surface the failed recovery and resume reporting.
            try {
                captureMessage?.("Preload recovery reload did not unload the document", {
                    level: "warning",
                    tags: { error_source: "vite_preload" },
                    contexts: {
                        preload_recovery: { ...context, outcome: "reload_did_not_unload" }
                    }
                });
            } catch {
                // Keep telemetry failure independent from application behavior.
            }
        }, RELOAD_PENDING_MS);
    };

    windowRef.addEventListener("vite:preloadError", onPreloadError);
    return () => {
        windowRef.removeEventListener("vite:preloadError", onPreloadError);
        clearTimeout(pendingReloadTimer);
        reloadingWindows.delete(windowRef);
    };
}
